<script setup lang="ts">
  defineOptions({ name: "AppInput", inheritAttrs: false })

  const props = withDefaults(
    defineProps<{
      /** Input value (v-model) */
      modelValue?: string | number
      /** Input type */
      type?: "text" | "password" | "email" | "number" | "tel" | "url" | "search"
      /** Placeholder text */
      placeholder?: string
      /** Minimum value (for type="number") */
      min?: number
      /** Maximum value (for type="number") */
      max?: number
      /** Autofocus on mount */
      autofocus?: boolean
      /** Mark as required */
      required?: boolean
      /** Disabled state */
      disabled?: boolean
      /** Error state (shows red border) */
      error?: boolean
      /** Name attribute */
      name?: string
      /** ID attribute */
      id?: string
    }>(),
    {
      modelValue: undefined,
      type: "text",
      placeholder: undefined,
      min: undefined,
      max: undefined,
      autofocus: false,
      required: false,
      disabled: false,
      error: false,
      name: undefined,
      id: undefined,
    },
  )

  const emit = defineEmits<{
    (e: "update:modelValue", value: string | number): void
  }>()

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value = props.type === "number" ? Number(target.value) : target.value
    emit("update:modelValue", value)
  }
</script>

<template>
  <div class="app-input" :class="{ 'app-input--error': error }">
    <div v-if="$slots.leading" class="app-input__leading">
      <slot name="leading" />
    </div>
    <input
      v-bind="$attrs"
      :id="id"
      :value="modelValue"
      :type="type"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :autofocus="autofocus"
      :required="required"
      :disabled="disabled"
      :name="name"
      class="app-input__field"
      @input="handleInput"
    />
    <div v-if="$slots.trailing" class="app-input__trailing">
      <slot name="trailing" />
    </div>
  </div>
</template>

<style scoped>
  .app-input {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--app-input-bg, #0f172a);
    border: 1px solid var(--app-input-border, rgba(255, 255, 255, 0.15));
    border-radius: var(--app-input-radius, 0.375rem);
    padding: 0.5rem 0.75rem;
    transition: all 0.2s;
  }

  .app-input:focus-within {
    border-color: var(--app-input-focus-border, rgba(96, 165, 250, 0.5));
    background: var(--app-input-focus-bg, #0f172a);
  }

  .app-input--error {
    border-color: var(--app-input-error-border, rgba(239, 68, 68, 0.6));
  }

  .app-input--error:focus-within {
    border-color: var(--app-input-error-focus-border, rgba(239, 68, 68, 0.8));
  }

  .app-input__field {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--app-input-color, #fff);
    font-size: inherit;
    font-family: inherit;
    width: 100%;
  }

  .app-input__field::placeholder {
    color: var(--app-input-placeholder, rgba(255, 255, 255, 0.4));
  }

  .app-input__field:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .app-input__leading,
  .app-input__trailing {
    display: flex;
    align-items: center;
    color: var(--app-input-icon-color, rgba(255, 255, 255, 0.5));
  }

  /* Number input spinners */
  .app-input__field::-webkit-outer-spin-button,
  .app-input__field::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .app-input__field[type="number"] {
    -moz-appearance: textfield;
  }
</style>
